import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { normalizeFieldReport } from '@/mobile/sync/normalizeFieldReport';
import {
  isCancelledFieldReport,
  withoutCancelledFieldReports,
} from '@/modules/field/utils/fieldReportCancelled';
import type { FieldReportListItem } from '@/modules/field/types';

const WORKER_MERGE_THRESHOLD = 120;

function mergeListById(
  existing: FieldReportListItem[],
  incoming: FieldReportListItem[],
): FieldReportListItem[] {
  if (incoming.length === 0) {
    return existing;
  }

  const map = new Map<number, FieldReportListItem>();
  for (const item of existing) {
    if (!isCancelledFieldReport(item)) {
      map.set(item.id, item);
    }
  }
  for (const item of incoming) {
    if (isCancelledFieldReport(item)) {
      map.delete(item.id);
      continue;
    }

    map.set(item.id, item);
  }

  return withoutCancelledFieldReports(Array.from(map.values()));
}

export async function mergeFieldReportLists(
  existing: FieldReportListItem[],
  incoming: FieldReportListItem[],
): Promise<FieldReportListItem[]> {
  const normalizedIncoming = incoming.map((report) => normalizeFieldReport(report));

  if (normalizedIncoming.length === 0) {
    return existing;
  }

  if (
    typeof Worker === 'undefined'
    || isBundledMobileApp()
    || existing.length < WORKER_MERGE_THRESHOLD
  ) {
    return mergeListById(existing, normalizedIncoming).map((report) =>
      normalizeFieldReport(report),
    );
  }

  try {
    const worker = new Worker(new URL('./fieldCacheMerge.worker.ts', import.meta.url), {
      type: 'module',
    });

    const merged = await new Promise<FieldReportListItem[]>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<FieldReportListItem[]>) => {
        worker.terminate();
        resolve(
          event.data.map((report) => normalizeFieldReport(report)),
        );
      };

      worker.onerror = (error) => {
        worker.terminate();
        reject(error);
      };

      worker.postMessage({ existing, incoming: normalizedIncoming });
    });

    return merged;
  } catch {
    return mergeListById(existing, normalizedIncoming).map((report) =>
      normalizeFieldReport(report),
    );
  }
}
